import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { inr, fdate } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { SearchInput } from "@/components/pawkt/SearchInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, IndianRupee, Trash2 } from "lucide-react";
import { InvoiceRowMenu } from "@/components/pawkt/InvoiceRowMenu";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { DiscardInvoiceModal } from "@/components/pawkt/DiscardInvoiceModal";
import { useRangeParams, apiQueryString, DrillDownBanner } from "@/lib/drilldown";
import { toast } from "sonner";

export default function Invoices() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "invoices.edit");
  const canCollect = hasPerm(user, "payments.create");
  const range = useRangeParams();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");
  const [filter, setFilter] = useState(range.status && range.status !== "outstanding" ? range.status : "");
  const [pay, setPay] = useState(null);
  const [edit, setEdit] = useState(null);
  const [discard, setDiscard] = useState(null);
  const [reissue, setReissue] = useState(null);
  const [correct, setCorrect] = useState(null);
  const menuHandlers = { onEdit: setEdit, onReissue: setReissue, onCorrect: setCorrect, onDiscard: setDiscard };

  const { data, isLoading } = useQuery({
    queryKey: ["invoices", filter, search, range.status, range.period, range.start, range.end],
    queryFn: async () => {
      const params = {};
      const st = filter || (range.status === "outstanding" ? "outstanding" : "");
      if (st) params.status = st;
      if (search) params.search = search;
      if (range.period) {
        params.period = range.period;
        if (range.period === "custom") { params.start = range.start; params.end = range.end; }
      }
      return (await api.get(`/invoices${apiQueryString(params)}`)).data;
    },
  });

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Billing history & collections" />
      <DrillDownBanner metric={range.status === "outstanding" ? "outstanding" : "invoices"} count={data?.total} />
      <div className="mb-4 max-w-md">
        <SearchInput value={search} onChange={setSearch} testid="invoices-search"
          placeholder="Search invoice no., customer name or mobile…" />
      </div>
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {["", "unpaid", "partially_paid", "paid", "refunded", "cancelled"].map((s) => (
          <button key={s || "all"} data-testid={`invoice-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium capitalize ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s ? s.replace("_", " ") : "All"}</button>
        ))}
      </div>

      {isLoading ? <Loading /> : (data?.items?.length ? (
        <>
        <div className="md:hidden space-y-2.5" data-testid="invoices-cards">
          {data.items.map((inv) => (
            <div key={inv.id} className="rounded-[18px] border border-pawkt-border bg-white p-4" data-testid={`invoice-card-${inv.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-slate-500">{inv.invoice_number}</div>
                  <div className="font-semibold text-slate-900 truncate">{inv.customer_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{fdate(inv.created_at)}</div>
                </div>
                <StatusBadge status={inv.status} />
              </div>
              <div className="mt-2.5 flex items-center justify-between text-sm">
                <span className="text-slate-500 text-xs">Total <b className="text-slate-900 text-sm tabular">{inr(inv.total)}</b></span>
                {inv.balance > 0 && <span className="text-amber-600 text-xs">Due <b className="tabular">{inr(inv.balance)}</b></span>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <InvoiceRowMenu invoice={inv} canEdit={canEdit} testidSuffix="m-" {...menuHandlers} />
                {canCollect && inv.balance > 0 && inv.status !== "cancelled" && <Button size="sm" data-testid={`collect-m-${inv.id}`} onClick={() => setPay(inv)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs gap-1 ml-auto"><IndianRupee className="h-3 w-3" /> Collect {inr(inv.balance)}</Button>}
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 hidden sm:table-cell">Date</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{inv.invoice_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{fdate(inv.created_at)}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular">{inr(inv.total)}</td>
                  <td className="px-4 py-3 text-right tabular text-amber-600">{inr(inv.balance)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {canCollect && inv.balance > 0 && inv.status !== "cancelled" && <Button size="sm" data-testid={`collect-${inv.id}`} onClick={() => setPay(inv)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs gap-1"><IndianRupee className="h-3 w-3" /> Collect</Button>}
                      <InvoiceRowMenu invoice={inv} canEdit={canEdit} {...menuHandlers} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <Empty icon={FileText} title={search ? "No matching invoices found" : "No invoices yet"} subtitle={search ? "Try a different name, mobile number or invoice number." : "Bill a booking from the POS to generate invoices."} />)}

      {pay && <CollectModal invoice={pay} canBackdate={hasPerm(user, "payments.backdate")} onClose={() => setPay(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setPay(null); }} />}
      {edit && <EditInvoiceModal invoice={edit} onClose={() => setEdit(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["invoices"] }); setEdit(null); }} />}
      {discard && <DiscardInvoiceModal invoice={discard} onClose={() => setDiscard(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setDiscard(null); }} />}
      {reissue && <ReissueModal invoice={reissue} onClose={() => setReissue(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setReissue(null); }} />}
      {correct && <CorrectInvoiceModal invoice={correct} onClose={() => setCorrect(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setCorrect(null); }} />}
    </div>
  );
}

function EditInvoiceModal({ invoice, onClose, onDone }) {
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const [items, setItems] = useState((invoice.items || []).filter((it) => it.kind === "service").map((it) => ({ ref_id: it.ref_id, name: it.name, qty: it.qty || 1 })));
  const [busy, setBusy] = useState(false);
  const list = services?.items || services || [];
  const addService = (sid) => { const s = list.find((x) => x.id === sid); if (s) setItems((p) => [...p, { ref_id: s.id, name: s.name, qty: 1 }]); };
  const setQty = (i, q) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, qty: Math.max(1, Number(q) || 1) } : it));
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const est = items.reduce((a, it) => a + (list.find((x) => x.id === it.ref_id)?.price || 0) * it.qty, 0);
  const submit = async () => {
    if (!items.length) { toast.error("Add at least one service"); return; }
    setBusy(true);
    try {
      await api.patch(`/invoices/${invoice.id}`, { items: items.map((it) => ({ kind: "service", ref_id: it.ref_id, name: it.name, qty: it.qty })), expected_updated_at: invoice.updated_at });
      toast.success("Invoice updated"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="edit-invoice-modal">
        <DialogHeader><DialogTitle className="font-display">Edit invoice · {invoice.invoice_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Only unpaid, service-only invoices can be edited. The invoice number stays the same and the total is re-priced server-side.</p>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto" data-testid="edit-items">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`edit-item-${i}`}>
              <span className="flex-1 truncate">{it.name}</span>
              <Input type="number" min="1" value={it.qty} onChange={(e) => setQty(i, e.target.value)} className="h-8 w-16" data-testid={`edit-qty-${i}`} />
              <button onClick={() => remove(i)} className="text-rose-500 text-xs" data-testid={`edit-remove-${i}`}>Remove</button>
            </div>
          ))}
        </div>
        <Select onValueChange={addService}><SelectTrigger className="bg-white" data-testid="edit-add-service"><SelectValue placeholder="+ Add a service" /></SelectTrigger>
          <SelectContent className="bg-white">{list.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {inr(s.price)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="rounded-lg bg-slate-50 p-3 text-sm flex justify-between"><span className="text-slate-500">Estimated subtotal</span><span className="font-semibold" data-testid="edit-est">{inr(est)}</span></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReissueModal({ invoice, onClose, onDone }) {
  const [form, setForm] = useState({
    display_name: invoice.bill_to?.display_name || invoice.customer_name || "",
    company_name: invoice.bill_to?.company_name || "",
    billing_address: invoice.bill_to?.billing_address || "",
    gstin: invoice.bill_to?.gstin || "",
    email: invoice.bill_to?.email || "",
    phone: invoice.bill_to?.phone || "",
  });
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("edit"); // edit | review
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const orig = {
    display_name: invoice.bill_to?.display_name || invoice.customer_name || "",
    company_name: invoice.bill_to?.company_name || "",
    billing_address: invoice.bill_to?.billing_address || "",
    gstin: invoice.bill_to?.gstin || "", email: invoice.bill_to?.email || "", phone: invoice.bill_to?.phone || "",
  };
  const FIELDS = [["display_name", "Customer / display name"], ["company_name", "Company / legal name"],
    ["gstin", "GSTIN"], ["billing_address", "Billing address"], ["email", "Email"], ["phone", "Phone"]];
  const changed = FIELDS.filter(([k]) => (form[k] || "") !== (orig[k] || ""));
  const submit = async () => {
    if (!reason.trim()) { toast.error("A correction reason is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/reissue`, {
        ...form, reason: reason.trim(), confirm: true, expected_updated_at: invoice.updated_at });
      toast.success(`Reissued as ${data.replacement?.invoice_number}`); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="reissue-modal">
        <DialogHeader><DialogTitle className="font-display">Edit &amp; Reissue · {invoice.invoice_number}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-400 -mt-2">Corrects billing details only (name, company, GSTIN, address, email, phone) — items, quantities, prices, tax and total stay unchanged. The original is cancelled &amp; superseded (kept for audit) and a new invoice with a new number is issued carrying the same amounts and any payments already made.</DialogDescription>
        {Number(invoice.amount_paid || 0) > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 space-y-1" data-testid="reissue-paid-warning">
            <div><b>₹{Number(invoice.amount_paid || 0).toLocaleString("en-IN")} already collected</b> will be carried over to the new invoice — no money is re-collected and payment records are preserved.</div>
            <div className="text-amber-700">GST note: this assumes the invoice has <b>not</b> yet been reported in a filed GST return. If it has, use a credit/debit note (accounting review) instead.</div>
          </div>
        )}
        {step === "edit" ? (
          <div className="space-y-2.5 max-h-[50vh] overflow-y-auto">
            {FIELDS.map(([k, label]) => (
              <div key={k}><Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
                {k === "billing_address"
                  ? <Textarea value={form[k]} onChange={set(k)} rows={2} data-testid={`reissue-${k}`} className="bg-white" />
                  : <Input value={form[k]} onChange={set(k)} data-testid={`reissue-${k}`} className="bg-white" />}
              </div>
            ))}
            <div><Label className="text-xs text-amber-700 mb-1 block">Correction reason <span className="text-rose-500">*</span></Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="reissue-reason" className="bg-white" placeholder="Why is this billing detail being corrected?" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => { if (!reason.trim()) { toast.error("A correction reason is required"); return; } setStep("review"); }} data-testid="reissue-next" className="bg-slate-900 text-white">Review changes</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reissue-review">
            {changed.length === 0 ? (
              <div className="text-sm text-amber-600">No billing details changed. Go back and edit a field, or cancel.</div>
            ) : (
              <div className="rounded-lg border border-slate-200 divide-y text-sm">
                {changed.map(([k, label]) => (
                  <div key={k} className="p-2.5" data-testid={`reissue-diff-${k}`}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
                    <div className="text-rose-600 line-through truncate">{orig[k] || "—"}</div>
                    <div className="text-emerald-700 font-medium truncate">{form[k] || "—"}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg bg-slate-50 p-2.5 text-xs text-slate-500">Reason: <span className="text-slate-800">{reason}</span></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("edit")} data-testid="reissue-back">Back</Button>
              <Button onClick={submit} disabled={busy || changed.length === 0} data-testid="reissue-confirm" className="bg-slate-900 text-white">{busy ? "Reissuing…" : "Confirm & Reissue"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CorrectInvoiceModal({ invoice, onClose, onDone }) {
  const { data: services } = useQuery({ queryKey: ["svc-min"], queryFn: async () => (await api.get("/services")).data });
  const { data: products } = useQuery({ queryKey: ["prod-min"], queryFn: async () => (await api.get("/products")).data });
  const svcList = services?.items || services || [];
  const prodList = products?.items || products || [];
  const [rows, setRows] = useState(() => (invoice.items || []).map((l) => ({
    kind: l.kind || "service", ref_id: l.ref_id || "", name: l.name, qty: Number(l.qty || 1),
  })));
  const [discount, setDiscount] = useState(Number(invoice.manual_discount || invoice.discount || 0));
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [step, setStep] = useState("edit");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const collected = Number(invoice.amount_paid || 0);
  const needsAck = ["partially_paid", "paid"].includes(invoice.status);

  const setQty = (i, v) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, qty: Math.max(1, Number(v) || 1) } : x)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addItem = (kind, ref_id) => {
    const src = kind === "service" ? svcList.find((s) => s.id === ref_id) : prodList.find((p) => p.id === ref_id);
    if (!src) return;
    setRows((r) => [...r, { kind, ref_id, name: src.name, qty: 1 }]);
  };
  const payload = (extra) => ({
    items: rows.map((r) => ({ kind: r.kind, ref_id: r.ref_id, name: r.name, qty: r.qty })),
    discount: Number(discount) || 0, reason: reason.trim(), confirm: true, accounting_ack: ack,
    expected_updated_at: invoice.updated_at, ...extra,
  });
  const doPreview = async () => {
    if (rows.length === 0) { toast.error("Add at least one item"); return; }
    if (!reason.trim()) { toast.error("A correction reason is required"); return; }
    if (needsAck && !ack) { toast.error("Please acknowledge the GST / accounting note to continue"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/correct`, payload({ preview: true }));
      setPreview(data); setStep("review");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/correct`, payload());
      toast.success(`Corrected → ${data.replacement?.invoice_number}`); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const C = preview?.corrected, O = preview?.original;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="correct-modal">
        <DialogHeader><DialogTitle className="font-display">Correct Invoice · {invoice.invoice_number}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-400 -mt-2">Fix a genuine billing mistake — add/remove items, change quantities or discount. Prices &amp; tax are recomputed from the catalog. The original is kept &amp; superseded; a new invoice is issued. Payments already made are carried over.</DialogDescription>
        {needsAck && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="correct-paid-warning">
            ₹{collected.toLocaleString("en-IN")} already collected is carried over — no money is re-collected. GST note: a paid tax invoice reported in a filed return must be corrected via a credit/debit note; proceed only after accounting review.
          </div>
        )}
        {step === "edit" ? (
          <div className="space-y-2.5 max-h-[55vh] overflow-y-auto">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`correct-row-${i}`}>
                <span className="flex-1 text-sm text-slate-700 truncate">{r.name} <span className="text-[10px] uppercase text-slate-400">{r.kind}</span></span>
                <Input type="number" min="1" value={r.qty} onChange={(e) => setQty(i, e.target.value)} data-testid={`correct-qty-${i}`} className="w-16 h-8 bg-white" />
                <Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`correct-remove-${i}`} className="h-8 text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Select onValueChange={(v) => addItem("service", v)}><SelectTrigger data-testid="correct-add-service" className="h-8 bg-white text-xs"><SelectValue placeholder="+ Add service" /></SelectTrigger>
                <SelectContent>{svcList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
              <Select onValueChange={(v) => addItem("product", v)}><SelectTrigger data-testid="correct-add-product" className="h-8 bg-white text-xs"><SelectValue placeholder="+ Add product" /></SelectTrigger>
                <SelectContent>{prodList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Manual discount (₹)</Label>
              <Input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="correct-discount" className="bg-white" /></div>
            <div><Label className="text-xs text-amber-700 mb-1 block">Correction reason <span className="text-rose-500">*</span></Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="correct-reason" className="bg-white" placeholder="What was billed wrong and why is it being corrected?" /></div>
            {needsAck && (
              <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="correct-ack-label">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="correct-ack" className="mt-0.5" />
                <span>I confirm this correction has been reviewed for accounting / GST compliance.</span>
              </label>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={doPreview} disabled={busy} data-testid="correct-next" className="bg-slate-900 text-white">{busy ? "Calculating…" : "Review changes"}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="correct-review">
            <div className="rounded-lg border border-slate-200 text-sm overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400 px-3 py-1.5"><span></span><span className="text-right">Before</span><span className="text-right">After</span></div>
              {[["Subtotal", O?.subtotal, C?.subtotal], ["Discount", O?.discount, C?.discount], ["Tax", O?.tax, C?.tax], ["Total", O?.total, C?.total], ["Collected", O?.collected, C?.collected], ["Balance due", O?.balance, C?.balance]].map(([k, a, b]) => (
                <div key={k} className="grid grid-cols-3 px-3 py-1.5 border-t border-slate-100" data-testid={`correct-diff-${k.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="text-slate-500">{k}</span><span className="text-right text-slate-400 tabular">{inr(a || 0)}</span><span className="text-right font-medium text-slate-900 tabular">{inr(b || 0)}</span>
                </div>
              ))}
              <div className="grid grid-cols-3 px-3 py-1.5 border-t border-slate-100"><span className="text-slate-500">New status</span><span className="text-right text-slate-400 capitalize">{O?.status?.replace("_", " ")}</span><span className="text-right font-medium capitalize" data-testid="correct-new-status">{C?.status?.replace("_", " ")}</span></div>
            </div>
            <div className="rounded-lg bg-slate-50 p-2.5 text-xs text-slate-500">Reason: <span className="text-slate-800">{reason}</span></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("edit")} data-testid="correct-back">Back</Button>
              <Button onClick={submit} disabled={busy} data-testid="correct-confirm" className="bg-slate-900 text-white">{busy ? "Correcting…" : "Confirm correction"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CollectModal({ invoice, canBackdate, onClose, onDone }) {
  const today = new Date().toLocaleDateString("en-CA");
  const [amount, setAmount] = useState(invoice.balance);
  const [method, setMethod] = useState("upi");
  const [payDate, setPayDate] = useState(today);
  const [backdateReason, setBackdateReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [onlineBusy, setOnlineBusy] = useState(false);
  const { data: gateway } = useQuery({ queryKey: ["gateway-status"], queryFn: async () => (await api.get("/payments/gateway-status")).data });
  const razorpayOn = !!gateway?.razorpay?.connected;
  const isBackdated = payDate && payDate < today;
  const submit = async () => {
    if (payDate && payDate > today) { toast.error("Payment date cannot be in the future"); return; }
    if (isBackdated && !backdateReason.trim()) { toast.error("A reason is required to backdate a payment"); return; }
    setBusy(true);
    try { await api.post("/payments", { invoice_id: invoice.id, amount: Number(amount), method, payment_date: payDate || "", backdate_reason: isBackdated ? backdateReason.trim() : "" }); toast.success("Payment recorded"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // Razorpay online payment — identical flow to POS: amount/order are backend-authoritative
  // (POST /payments/create-order derives from invoice balance), signature verified server-side
  // (POST /payments/verify), and settlement goes through the existing ledger. No new endpoint.
  const payOnline = async () => {
    if (onlineBusy) return;
    setOnlineBusy(true);
    const fail = (m) => { toast.error(m); setOnlineBusy(false); };
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) return fail("Unable to start online payment. Please try again.");
      let order;
      try { order = (await api.post("/payments/create-order", { invoice_id: invoice.id, provider: "razorpay" })).data; }
      catch (e) { return fail(apiErr(e) || "Unable to start online payment. Please try again."); }
      if (!order?.order_id || !order?.key_id) return fail("Online payment is not available right now.");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount_paise, currency: order.currency || "INR", order_id: order.order_id,
        name: invoice.business_name || "Pawkt", description: `Invoice ${order.invoice_number}`,
        prefill: { name: invoice.customer_name || "", email: invoice.customer_email || "", contact: invoice.customer_mobile || "" },
        theme: { color: "#6A48F0" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { invoice_id: invoice.id, razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
            toast.success("Payment successful"); onDone();
          } catch (e) { toast.error("Payment verification failed. Please contact the business before retrying."); }
          finally { setOnlineBusy(false); }
        },
        modal: { ondismiss: () => { setOnlineBusy(false); toast.message("Payment cancelled."); }, escape: true },
      });
      rzp.on("payment.failed", () => { toast.error("Payment was not completed."); });
      rzp.open();
    } catch (e) { fail("Unable to start online payment. Please try again."); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="collect-modal">
        <DialogHeader><DialogTitle className="font-display">Collect payment · {invoice.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 p-3 text-sm flex justify-between"><span className="text-slate-500">Balance due</span><span className="font-semibold">{inr(invoice.balance)}</span></div>
          {razorpayOn && (
            <Button onClick={payOnline} disabled={busy || onlineBusy} data-testid="collect-pay-online" className="w-full bg-pawkt-primary hover:bg-pawkt-primary-hover text-white gap-1.5">
              {onlineBusy ? "Starting…" : `Pay Online (Razorpay) · ${inr(invoice.balance)}`}
            </Button>
          )}
          {razorpayOn && <div className="relative text-center"><span className="bg-white px-2 text-[11px] uppercase tracking-wide text-slate-400">or record manually</span><div className="absolute inset-x-0 top-1/2 -z-10 h-px bg-slate-200" /></div>}
          <div><Label className="text-xs text-slate-500 mb-1 block">Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="collect-amount" className="bg-white" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Method</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger className="bg-white" data-testid="collect-method"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["upi", "cash", "card", "bank", "online"].map((m) => <SelectItem key={m} value={m} className="uppercase">{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Payment date</Label>
            <Input type="date" value={payDate} max={today} min={canBackdate ? undefined : today}
              onChange={(e) => setPayDate(e.target.value)} data-testid="collect-payment-date" className="bg-white" />
            {!canBackdate && <p className="mt-1 text-[11px] text-slate-400">Defaults to today. Backdating requires permission.</p>}
          </div>
          {isBackdated && (
            <div data-testid="collect-backdate-block"><Label className="text-xs text-amber-700 mb-1 block">Reason for backdating <span className="text-rose-500">*</span></Label>
              <Textarea value={backdateReason} onChange={(e) => setBackdateReason(e.target.value)} rows={2}
                placeholder="Why is this payment dated earlier than today?" data-testid="collect-backdate-reason" className="bg-white" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || onlineBusy} data-testid="collect-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Recording…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
